import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { IOrderLeasePrices, IOrderPermissions, IOrderWarning, OrderEntryDataService, ProcurementType } from '../order-entry-data.service';
import { OrderEntryWebshopService } from '../webshop/order-entry-webshop.service';
import { IPricesResponse, IPricesTotal, PriceCartService } from '../../cart/price-cart.service';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../../shared/formly/ga-formly.service';
import { UntypedFormGroup } from '@angular/forms';
import { ConfirmTcLabelComponent } from './confirm-tc-label.component';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { GaEventService, IGAEvent } from '../../../../shared/services/ga-event.service';
import { EVENT_ORDER_BUSY_STATUS_CHANGED } from '../../../../shared/app-events';
import { Subscription } from 'rxjs';
import { IObject } from '../../../../../type_definitions/datatypes';
import { OrderEntryConfirmService } from './order-entry-confirm.service';
import { OrderEntryService } from '../order-entry.service';
import { WIZARD_ORDER } from '../../../../shared/app-wizards';
import { GaWizard } from '../../../../shared/wizard/ga-wizard';
import { GaWizardService } from '../../../../shared/wizard/ga-wizard.service';
import { GaDeferredService, IGADeferred } from '../../../../shared/services/ga-deferred.service';
import { OrderEntryStepId } from '../order-entry-steps';
import { GaValidateService, IValidatingComponent } from '../../../../shared/formly/ga-validate.service';

interface IOrderEntryConfirmModel {
  agreeCharges:any;
  agreeTC:any;
  approvalReason:any;
}

@Component({
  selector: 'order-entry-confirm',
  template: `
    <h2 *ngIf="loading">
      <activity-indicator toLoad="ORDERENTRY.CONFIRM.TITLE"></activity-indicator>
    </h2>

    <div class="confirm" *ngIf="!loading">
      <h2 class="no-margin-top" *ngIf="!loading" translate>ORDERENTRY.CONFIRM.TITLE</h2>

      <div class="row">
        <div class="col-12 col-sm-7 col-md-8 warning-wrap">
          <div class="warning" *ngFor="let warning of warningText">
            <i class="fa fa-warning"></i>
            <div class="text-warning" [innerHTML]="warning | translate"></div>
          </div>
          <div class="warning" *ngIf="confirmationWarnings?.warnings">
            <i class="fa fa-warning"></i>
            <span [innerHTML]="confirmationWarnings.warnings"></span>
          </div>
        </div>

        <div class="col-12 col-sm-7 col-md-8 warning-wrap" *ngIf="approvalNeeded">
          <div class="warning">
            <i class="fa fa-warning"></i>
            <div class="text-warning">
              <span translate>ORDERENTRY.CONFIRM.PROVIDE_REASON</span>
            </div>
          </div>
          <formly [fields]="reasonFields" [model]="model" [form]="form" class="agree col-12 col-sm-7 col-md-8"></formly>
        </div>

        <div class="col-12 col-sm-5 col-md-4 confirm-wrap">
          <button *ngIf="orderPermissions?.allowSubmit" class="btn btn-success btn-lg" (click)="checkout()" [busyButton]="buttonBusy?.checkout" [disabled]="executing">
            <i class="fa fa-check" *ngIf="!executing"></i>
            <span translate>ORDERENTRY.CONFIRM.CONFIRM_SUBMIT</span>
          </button>
          <button *ngIf="orderPermissions?.allowToApprove" class="btn btn-success btn-lg" (click)="checkout()" [busyButton]="buttonBusy?.checkout" [disabled]="executing">
            <i class="fa fa-check" *ngIf="!executing"></i>
            <span translate>ORDERENTRY.CONFIRM.CONFIRM_APPROVAL</span>
          </button>
        </div>
      </div>

      <div class="agree row" *ngIf="needOrderConfirmation">
        <div class="col-md-12">
          <formly [fields]="confirmationFields" [model]="model" [form]="form"></formly>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12">
          <h2 translate>ORDERENTRY.CONFIRM.ORDER_OVERVIEW</h2>
          <order-entry-overview-items [hidePrices]="hidePrices" [purchasePrices]="purchasePrices" [leasePrices]="leasePrices" [showLease]="showLease"></order-entry-overview-items>
        </div>
      </div>

      <order-entry-documents [editable]="true" [showTitle]="true" [showUpload]="false" [orderId]="orderId"></order-entry-documents>
    </div>
  `,
  encapsulation: ViewEncapsulation.None,
  styleUrls: [ './order-entry-confirm.scss' ]
})
export class OrderEntryConfirmComponent implements IValidatingComponent, OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
  @Input() public orderId:number;

  public loading:boolean;
  public confirmationWarnings:{
    warnings:string;
  };
  public reasonFields:IExtraFieldConfigurationObject[];
  public confirmationFields:IExtraFieldConfigurationObject[];
  public agreeChargesField:IExtraFieldConfigurationObject;
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public model:IOrderEntryConfirmModel = <any>{};
  public approvalNeeded:boolean;
  public warningText:string[];
  public needOrderConfirmation:boolean;
  public purchasePrices:IPricesTotal;
  public leasePrices:IOrderLeasePrices<IPricesTotal>;
  public showLease:boolean;
  public hidePrices:boolean;
  public orderPermissions:IOrderPermissions;
  public executing:boolean;
  public buttonBusy:IObject;
  private wizard:GaWizard;
  private eventHandler:Subscription;
  private onValidationReadyDeferred:IGADeferred<void>; // resolved when the component is ready for validation

  constructor(
    private orderDataService:OrderEntryDataService,
    private orderEntryWebshopService:OrderEntryWebshopService,
    private orderEntryService:OrderEntryService,
    private priceCartService:PriceCartService,
    private formlyService:GaFormlyService,
    private translationService:TranslationService,
    private eventService:GaEventService,
    private orderEntryConfirmService:OrderEntryConfirmService,
    private changeDetectorRef:ChangeDetectorRef,
    private wizardService:GaWizardService,
    private validateService:GaValidateService,
    deferredService:GaDeferredService
  ) {
    this.wizard = this.wizardService.getInstance(WIZARD_ORDER);
    this.wizard.currentValidator(this.validate, true, this.beforeValidate, OrderEntryStepId.CONFIRM);
    this.onValidationReadyDeferred = deferredService.defer();
  }

  public ngOnInit():void {
    // initialize model
    this.model = {
      agreeCharges: this.orderEntryConfirmService.agreeCharges,
      agreeTC: this.orderEntryConfirmService.agreeTC,
      approvalReason: this.orderEntryConfirmService.approvalReason
    };

    // create formly fields
    this.reasonFields = this.createReasonFields();
    // set necessary context
    this.approvalNeeded = this.orderEntryWebshopService.shopContext?.approvalNeeded;
    this.hidePrices = this.orderEntryWebshopService.shopContext?.hidePrices;
    this.needOrderConfirmation = this.orderEntryWebshopService.shopContext?.needOrderConfirmation;
    this.orderPermissions = this.orderEntryWebshopService.shopContext?.orderPermissions;
    this.showLease = this.orderEntryWebshopService.shopContext?.defaultProcurementType === ProcurementType.LEASE;
    this.confirmationFields = this.createConfirmationFields();

    // trigger first time change
    this.onCartPriceChange();

    // load data
    this.loadWarnings();
    this.loadPrices();

    // register listener
    this.listen();
  }

  public ngAfterViewInit():void {
    this.onValidationReadyDeferred.resolve();
  }

  public ngAfterViewChecked():void {
    // force change detection because form control attributes value might change after initial detect change
    this.changeDetectorRef.detectChanges();
  }

  public checkout():void {
    this.orderEntryService.checkout();
  }

  private listen():void {
    this.eventHandler = this.eventService.events.subscribe((evt:IGAEvent) => {
      if (evt.name === EVENT_ORDER_BUSY_STATUS_CHANGED) {
        this.buttonBusy = evt.data?.buttonBusy;
        this.executing = evt.data?.executing;
      }
    });

    this.form.valueChanges.subscribe((model:IOrderEntryConfirmModel) => {
      this.orderEntryConfirmService.agreeCharges = model.agreeCharges;
      this.orderEntryConfirmService.agreeTC = model.agreeTC;

      if (this.approvalNeeded) {
        this.orderEntryConfirmService.approvalReason = model.approvalReason;
      }
    });
  }

  private onCartPriceChange = (cartPrices?:IPricesResponse):void => {
    this.purchasePrices = cartPrices?.purchasePriceTotals || (<IPricesResponse>this.priceCartService.prices)?.purchasePriceTotals;
    this.leasePrices = cartPrices?.leasePriceTotals || (<IPricesResponse>this.priceCartService.prices)?.leasePriceTotals;
    if (this.purchasePrices && this.orderEntryWebshopService.shopContext?.defaultOrderCurrency && !this.orderEntryWebshopService.shopContext.hidePrices) {
      this.warningText = this.buildWarningText();
    }
  };

  private loadWarnings():void {
    // warnings should not be loaded for customers that don't see prices
    if (this.orderEntryWebshopService.shopContext && !this.hidePrices) {
      const webshopId:number = this.orderEntryWebshopService.selectedShop.id;
      const countryId:number = this.orderEntryWebshopService.selectedCountry.id;
      const data:any = this.orderEntryService.prepareOrderForWarnings();

      this.orderDataService.warnings(webshopId, countryId, data, this.orderId)
        .then((warning:IOrderWarning) => {
          this.confirmationWarnings = warning;
        });
    }
  }

  private loadPrices():void {
    if (this.orderEntryWebshopService.selectedShop?.id) {
      this.loading = true;
      this.priceCartService.load(this.orderId)
        .then(this.onCartPriceChange)
        .finally(() => {
          this.loading = false;
        });
    }
  }

  private createReasonFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.anInput('approvalReason', 'Reason')
        .isRequired()
        .build()
    ];
  }

  private createConfirmationFields():IExtraFieldConfigurationObject[] {
    return [
      this.agreeChargesField = this.formlyService.aCheckbox('agreeCharges', 'ORDERENTRY.CONFIRM.AGREE')
        .withoutWrapper()
        .isRequired()
        .withValidationMessage('required', this.translationService.instant('ORDERENTRY.CONFIRM.PLEASE_AGREE_CHARGES'))
        .withHideExpression(() => !(this.needOrderConfirmation && (this.warningText?.length || this.confirmationWarnings?.warnings?.length)))
        .build(),
      this.formlyService.aCheckbox('agreeTC', 'ORDERENTRY.CONFIRM.TC')
        .withComponent(ConfirmTcLabelComponent)
        .withoutWrapper()
        .withExtraOptions({
          templateOptions: {
            inputs: {
              hidePrices: this.hidePrices,
              isRequired: true
            }
          }
        })
        .isRequired()
        .withValidationMessage('required', this.translationService.instant('ORDERENTRY.CONFIRM.PLEASE_AGREE_TC'))
        .withHint(this.translationService.instant('ORDERENTRY.CONFIRM.TC_INFO'))
        .withoutInfoOffset()
        .withInfoClass('row col-sm-12')
        .withHideExpression(() => !this.needOrderConfirmation)
        .build()
    ];
  }

  private buildWarningText():string[] {
    const warningtext:string[] = [];
    if (!(<IPricesResponse>this.priceCartService.prices)?.purchasePriceTotals?.shipping) {
      warningtext.push('ORDERENTRY.CONFIRM.WARN_CHARGES_NOT_INCLUDED');
    }

    // currency warnings
    if (this.priceCartService.currency !== this.orderEntryWebshopService.shopContext.defaultOrderCurrency.isoCode) {
      warningtext.unshift('ORDERENTRY.CONFIRM.WARN_CURRENCY_WRONG');
    }
    return warningtext;
  }

  public beforeValidate = ():Promise<any> => {
    return this.onValidationReadyDeferred.promise;
  };

  public validate = ():Promise<void|string[]> => {
    const errors:string[] = [];
    if (this.approvalNeeded && !this.model.approvalReason) {
      errors.push(this.translationService.instant('ORDERENTRY.CONFIRM.PROVIDE_REASON'));
    }

    return this.validateService.validateFields(this.confirmationFields, this.model, true, errors);
  };

  public ngOnDestroy():void {
    this.eventHandler?.unsubscribe();
    this.wizard.clearCurrentValidator();
  }
}